/**
 * Unified Predictive Search Component
 * 
 * Combines both original scripts without modifying their core logic
 * Only resolves the class name conflict
 */

class UnifiedPredictiveSearch extends SearchForm {
  constructor() {
    super();
    
    // Common properties
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    
    // Detect modal type
    this.modalType = this.detectModalType();
    
    // Initialize based on type
    if (this.modalType === 'box') {
      this.initBoxModal();
    } else {
      this.initStandardModal();
    }
  }

  /**
   * Detect modal type based on DOM
   */
  detectModalType() {
    const hasBoxModal = this.querySelector('.predictive-search__box-modal');
    const hasBoxPreContent = this.querySelector('.predictive-search__box-pre-content');
    const isHeaderSearch = this.classList.contains('search--predictive-in-header');
    
    return (hasBoxModal || hasBoxPreContent || isHeaderSearch) ? 'box' : 'standard';
  }

  // ===== STANDARD MODAL (Original predictive-search.js logic) =====
  initStandardModal() {
    this.setupStandardEventListeners();
  }

  setupStandardEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('input', this.onChange.bind(this));
    // Removed focusout for sidebar - it should stay open
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  // ===== BOX MODAL (Original predictive-search-2.js logic) =====
  initBoxModal() {
    this.searchInput = this.querySelector('.search__input');
    this.searchToggle = this.querySelector('.top-search-toggle');
    this.searchStaticResult = this.querySelector('.predictive-search__box-pre-content');
    this.isHandlingFocusEvent = false;

    this.setupModalBehavior();
    this.setupBoxEventListeners();
  }

  setupModalBehavior() {
    if (this.searchInput || this.searchToggle) {
      this.searchInput.addEventListener('click', (e) => {
        this.isHandlingFocusEvent = true;
        this.open();
        setTimeout(() => {
          this.isHandlingFocusEvent = false;
        }, 100);
      });

      this.searchInput.addEventListener('focus', (e) => {
        this.open();
        this.isHandlingFocusEvent = true;
        setTimeout(() => {
          this.isHandlingFocusEvent = false;
        }, 100);
      });
    }
  }

  setupBoxEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onBoxFocus.bind(this));
    this.input.addEventListener('input', this.onChange.bind(this));
    document.addEventListener('click', this.onFocusIn.bind(this));
    document.addEventListener('focusin', this.onFocusIn.bind(this));
    this.addEventListener('keyup', this.onBoxKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  // ===== SHARED METHODS =====
  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    
    // Show/hide reset button based on input content
    const resetButton = this.querySelector('.reset__button');
    if (resetButton) {
      if (newSearchTerm.length > 0) {
        resetButton.classList.remove('hidden');
      } else {
        resetButton.classList.add('hidden');
      }
    }
    
    // Handle empty search term properly for both modal types
    if (!newSearchTerm.length) {
      this.searchTerm = '';
      
      if (this.modalType === 'standard') {
        // For sidebar modal - clear results but keep modal open
        this.removeAttribute('results');
        this.removeAttribute('loading');
        if (this.predictiveSearchResults) {
          this.predictiveSearchResults.innerHTML = '';
        }
        // Clear any selected options
        const selected = this.querySelector('[aria-selected="true"]');
        if (selected) selected.setAttribute('aria-selected', false);
        
        this.input.setAttribute('aria-activedescendant', '');
        
        // Clear status element
        if (this.statusElement) {
          this.statusElement.textContent = '';
          this.statusElement.setAttribute('aria-hidden', 'true');
        }
      } else {
        // For box modal - close completely
        this.close(true);
      }
      return;
    }
    
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    // Handle form reset properly for both modal types
    if (this.modalType === 'standard') {
      // For standard modal, clear results but keep modal open
      this.searchTerm = '';
      this.removeAttribute('results');
      this.removeAttribute('loading');
      
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.innerHTML = '';
      }
      
      // Clear any selected options
      const selected = this.querySelector('[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', false);
      
      this.input.setAttribute('aria-activedescendant', '');
      
      // Clear status element
      if (this.statusElement) {
        this.statusElement.textContent = '';
        this.statusElement.setAttribute('aria-hidden', 'true');
      }
      
      // Hide reset button after clearing
      const resetButton = this.querySelector('.reset__button');
      if (resetButton) {
        resetButton.classList.add('hidden');
      }
      
    } else {
      // For box modal, use original logic
      super.onFormReset(event);
      if (super.shouldResetForm()) {
        this.searchTerm = '';
        this.abortController.abort();
        this.abortController = new AbortController();
        this.closeResults(true);
      }
    }
  }

  // ===== STANDARD MODAL SPECIFIC METHODS =====
  onFocus() {
    if (this.modalType !== 'standard') return;
    
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    // This method is intentionally empty for standard modal
    // Standard modal should not close on focus out
    return;
  }

  onKeyup(event) {
    if (this.modalType !== 'standard') return;
    
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  // ===== BOX MODAL SPECIFIC METHODS =====
  onBoxFocus(event) {
    if (this.modalType !== 'box') return;
    
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;
    
    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusIn(event) {
    if (this.modalType !== 'box') return;
    
    setTimeout(() => {
      if (this.isHandlingFocusEvent) return;

      const target = event.target;
      const isSearchContainer = target.closest('.ht-search-modal');
      const isSearchModal = target.closest('.predictive-search__box-modal') || 
                           target.classList.contains('predictive-search__box-modal');

      if (isSearchContainer || isSearchModal) {
        return;
      }

      this.classList.remove('search-modal-box-style');
      this.close();
    }, 100);
  }

  onBoxKeyup(event) {
    if (this.modalType !== 'box') return;
    
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    if (event.code === 'Escape') {
      this.searchInput.focus();
      this.close();
      this.classList.remove('search-modal-box-style');
    }

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  // ===== SHARED UTILITY METHODS =====
  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    
    if (currentButtonText) {
      const matches = currentButtonText.match(new RegExp(previousTerm, 'g'));
      if (matches && matches.length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(
      this.querySelectorAll('li, button.predictive-search__item')
    ).filter((element) => element.offsetParent !== null);
    
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(
      this.querySelector('[data-predictive-search-live-region-count-value]').textContent
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - 
      document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (this.isOpen) return;

    this.predictiveSearchResults.style.maxHeight = 
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;

    // Box modal specific
    if (this.modalType === 'box') {
      this.classList.add('search-modal-box-style');
      if (!this.querySelector('main-search')) {
        document.body.classList.add('search-modal-box-open');
      }
    }
  }

  close(clearSearchTerm = false) {
    if (!this.isOpen) return;

    this.closeResults(clearSearchTerm);
    this.isOpen = false;

    // Box modal specific
    if (this.modalType === 'box') {
      document.body.classList.remove('search-modal-box-open');
    }
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', UnifiedPredictiveSearch);